import React, { useEffect } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Container from '@mui/material/Container';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Link as RouterLink } from 'react-router-dom';
import { ROUTES } from './routes';

const sections = [
  {
    title: 'Create your account',
    content: (
      <>
        <Typography>
          Choose <strong>Login</strong>, then follow the registration link. You can create an account with an
          email address and password or choose <strong>Register with Google</strong>.
        </Typography>
        <Typography sx={{ mt: 1.25 }}>
          Email-and-password accounts must be verified before cloud journeys are available. Open the
          verification email, follow its link, return to JourneyBot, and choose <strong>I&apos;ve verified my
          email</strong>. Then sign in if prompted. Google accounts do not use this email-verification step.
        </Typography>
      </>
    ),
  },
  {
    title: 'Create a journey with AI',
    content: (
      <>
        <Typography>
          On the <strong>Journey Board</strong>, choose <strong>Create a journey with AI</strong>. Tell
          JourneyBot what you want to achieve in the <strong>Message JourneyBot</strong> box, then answer its
          focused questions so it can understand your starting point, desired outcome, constraints, and useful
          next actions.
        </Typography>
        <Typography sx={{ mt: 1.25 }}>
          If you are signed in, choose <strong>Create journey draft</strong>, review the suggested actions, and
          choose <strong>Add to journey board</strong> when the draft is ready. You can keep chatting and recreate
          the draft if you want to change it first.
        </Typography>
        <Typography sx={{ mt: 1.25 }}>
          If you are exploring as a guest, choose <strong>Create journey now</strong> when you are ready. JourneyBot
          opens a temporary, view-only preview; sign in and save it before you rely on it being available later.
        </Typography>
        <Alert severity="info" sx={{ mt: 2 }}>
          AI messages are processed to create your journey. Do not enter sensitive personal information, and
          review the generated journey before saving it.
        </Alert>
      </>
    ),
  },
  {
    title: 'Create or select a journey',
    content: (
      <>
        <Typography>
          Open <strong>Journey Board</strong>. Under <strong>Goal focus</strong>, choose a journey theme to
          focus the board. The selected chip is filled and marks the active journey; the
          <strong> Current journey</strong> heading shows its goal.
        </Typography>
        <Typography sx={{ mt: 1.25 }}>
          To create a journey from an AI response, open <strong>AI Import</strong>, choose
          <strong> Create new goal</strong>, enter the new goal title, and complete the import.
        </Typography>
      </>
    ),
  },
  {
    title: 'Define your journey',
    content: (
      <>
        <Typography>
          <strong>Goal focus</strong> is the short outcome the journey is organised around.
          <strong> Journey context</strong> records background that helps the plan make sense. Choose
          <strong> Edit</strong> beside Current journey before changing either field, then choose
          <strong> Save</strong>.
        </Typography>
        <Typography sx={{ mt: 1.25 }}>
          Goal focus accepts up to 7 words and Journey context accepts up to 300 characters. The board also
          asks where you are starting and what the end of the journey looks like.
        </Typography>
      </>
    ),
  },
  {
    title: 'Add steps',
    content: (
      <Typography>
        Steps break a journey into manageable stages. Choose <strong>Add Step</strong>, add a step title and
        description, and save it. Use <strong>Edit step</strong> or <strong>Delete step</strong> on a step,
        and use <strong>Move step left</strong> or <strong>Move step right</strong> to change its order.
      </Typography>
    ),
  },
  {
    title: 'Add action items',
    content: (
      <Typography>
        Action items are specific things you can do within a step. Choose <strong>Add action</strong> to add
        one. Select an action to reveal controls for editing, deleting, or moving it up or down. Use
        <strong> Mark action as complete</strong> to complete it, or <strong>Reopen action</strong> to make it
        active again. Steps with more than five actions show <strong>Show all</strong> and
        <strong> Show fewer</strong> controls.
      </Typography>
    ),
  },
  {
    title: 'Understand saving',
    content: (
      <>
        <Typography>
          JourneyBot keeps a working copy in the current browser and shows statuses such as
          <strong> Saved locally</strong>. Changes to an existing local journey are also autosaved in the
          browser; <strong>Save locally</strong> lets you save explicitly.
        </Typography>
        <Typography sx={{ mt: 1.25 }}>
          Signed-in users with a verified account can choose <strong>Save to cloud</strong>. Cloud saving is
          explicit: a local change is not sent to the account until that button succeeds. If local and cloud
          versions differ, JourneyBot asks which version to use and can keep both.
        </Typography>
        <Alert severity="warning" sx={{ mt: 2 }}>
          A journey saved only locally is not available in another browser or on another device. Clearing
          this browser&apos;s site data may remove it.
        </Alert>
      </>
    ),
  },
  {
    title: 'Return to a journey',
    content: (
      <Typography>
        Return to <strong>Journey Board</strong> and choose the journey under <strong>Goal focus</strong> to
        continue. Sign in first when you need a cloud journey. JourneyBot loads a browser working copy and,
        when local and cloud versions differ, asks you which one to use.
      </Typography>
    ),
  },
  {
    title: 'Import or export a journey',
    content: (
      <Typography>
        Choose <strong>Export JSON</strong> to download a copy of the current journey that you can keep.
        Choose <strong>Import JSON</strong> and select a Journey JSON file to replace the current local
        journey data with the imported copy. Importing does not merge the two journeys.
      </Typography>
    ),
  },
];

export default function WebAppGuide() {
  useEffect(() => {
    document.title = 'Use the JourneyBot Web App | JourneyBot';
    let description = document.querySelector('meta[name="description"]');
    if (!description) {
      description = document.createElement('meta');
      description.setAttribute('name', 'description');
      document.head.appendChild(description);
    }
    description.setAttribute('content', 'Learn how to create a journey with AI, edit it, save it, and import or export journeys in the JourneyBot web app.');
  }, []);

  return (
    <Box sx={{ bgcolor: 'background.default', color: 'text.primary', textAlign: 'left', py: { xs: 4, md: 7 } }}>
      <Container maxWidth="md">
        <Stack spacing={{ xs: 3, md: 4 }}>
          <Box component="header" sx={{ maxWidth: 780 }}>
            <Typography variant="overline" color="primary" sx={{ fontWeight: 800 }}>Ways to Use</Typography>
            <Typography variant="h2" component="h1" sx={{ fontWeight: 900, mt: 0.5 }}>Use the Web App</Typography>
            <Typography variant="h6" component="p" color="text.secondary" sx={{ mt: 2, maxWidth: 720, lineHeight: 1.6 }}>
              Create and manage your journeys directly in your browser. Start with a guided AI conversation or
              build your journey step by step, keep track of actions, and return whenever you are ready to continue.
            </Typography>
          </Box>

          <Stack spacing={2.5}>
            {sections.map(({ title, content }, index) => (
              <Card component="section" variant="outlined" key={title} data-testid="web-app-guide-step">
                <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <Chip label={index + 1} color="primary" aria-hidden="true" sx={{ fontWeight: 800, mt: 0.25 }} />
                    <Box sx={{ minWidth: 0, maxWidth: 760 }}>
                      <Typography variant="h5" component="h2" sx={{ fontWeight: 850, mb: 1.5 }}>{title}</Typography>
                      {content}
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>

          <Box component="section" aria-labelledby="chatgpt-guide-heading" sx={{ pt: 1 }}>
            <Typography id="chatgpt-guide-heading" variant="h4" component="h2" sx={{ fontWeight: 900 }}>
              Use JourneyBot with ChatGPT Plus
            </Typography>
            <Typography sx={{ mt: 1.5 }}>
              Prefer to build your journey through a conversation? See{' '}
              <Link component={RouterLink} to={ROUTES.waysToUseChatGpt}>Use with ChatGPT Plus</Link>.
            </Typography>
          </Box>
        </Stack>
      </Container>
    </Box>
  );
}
